import { useMemo } from 'react';
import {
  TrendingUp,
  DollarSign,
  ArrowUpCircle,
  AlertTriangle,
  CheckCircle,
  Lightbulb,
  Clock,
} from 'lucide-react';
import { clsx } from 'clsx';
import { Card, CardHeader, CardTitle, CardContent, Badge } from '../ui';
import { DatacenterConfig } from '../../types/configurator';
import {
  forecastResourceUsage,
  analyzeCostOptimization,
  recommendUpgradePaths,
  ResourceForecast,
  CostOptimization,
  UpgradePath,
  Bottleneck,
} from '../../utils/capacityPlanning';

interface CapacityPlanningPanelProps {
  config: DatacenterConfig;
  growthRate?: number;
}

export function CapacityPlanningPanel({
  config,
  growthRate = 10,
}: CapacityPlanningPanelProps) {
  const forecast = useMemo(
    () => forecastResourceUsage(config, growthRate, 12),
    [config, growthRate]
  );

  const costOptimization = useMemo(
    () => analyzeCostOptimization(config),
    [config]
  );

  const upgradePaths = useMemo(
    () => recommendUpgradePaths(config),
    [config]
  );

  if (config.nodes.length === 0) {
    return null;
  }

  return (
    <div className="space-y-4">
      {/* Overview Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        {/* Resource Forecast */}
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-blue-500" />
              <CardTitle className="text-sm">Resource Forecast</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <ForecastSummary forecast={forecast} />
          </CardContent>
        </Card>

        {/* Cost Optimization */}
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-green-500" />
              <CardTitle className="text-sm">Cost Savings</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <CostSummary optimization={costOptimization} />
          </CardContent>
        </Card>

        {/* Upgrade Paths */}
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <ArrowUpCircle className="h-4 w-4 text-purple-500" />
              <CardTitle className="text-sm">Upgrade Paths</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <UpgradeSummary paths={upgradePaths} />
          </CardContent>
        </Card>
      </div>

      {/* Detailed Sections */}
      <div className="grid gap-4 md:grid-cols-2">
        {/* Bottlenecks */}
        {forecast.bottlenecks.length > 0 && (
          <Card>
            <CardHeader className="py-3">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-500" />
                <CardTitle className="text-sm">Capacity Bottlenecks</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <BottleneckList bottlenecks={forecast.bottlenecks} />
            </CardContent>
          </Card>
        )}

        {/* Cost Suggestions */}
        <Card>
          <CardHeader className="py-3">
            <div className="flex items-center gap-2">
              <Lightbulb className="h-4 w-4 text-amber-500" />
              <CardTitle className="text-sm">Cost Optimization Tips</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            <SuggestionList suggestions={costOptimization.suggestions.slice(0, 4)} />
          </CardContent>
        </Card>

        {/* Resource Projection Chart */}
        <Card className="md:col-span-2">
          <CardHeader className="py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-blue-500" />
                <CardTitle className="text-sm">12-Month Resource Projection</CardTitle>
              </div>
              <span className="text-xs text-gray-500">
                Growth rate: {growthRate}%/year
              </span>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            <ResourceProjectionChart forecast={forecast} />
          </CardContent>
        </Card>

        {/* Upgrade Recommendations */}
        {upgradePaths.length > 0 && (
          <Card className="md:col-span-2">
            <CardHeader className="py-3">
              <div className="flex items-center gap-2">
                <ArrowUpCircle className="h-4 w-4 text-purple-500" />
                <CardTitle className="text-sm">Recommended Upgrades</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <UpgradePathList paths={upgradePaths} />
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

// Sub-components

function ForecastSummary({ forecast }: { forecast: ResourceForecast }) {
  const criticalCount = forecast.bottlenecks.filter(
    (b) => b.severity === 'critical' || b.severity === 'high'
  ).length;

  return (
    <div className="space-y-2">
      {criticalCount > 0 ? (
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-amber-500" />
          <div>
            <div className="text-lg font-bold text-amber-600">{criticalCount}</div>
            <div className="text-xs text-gray-500">capacity concerns</div>
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <CheckCircle className="h-5 w-5 text-green-500" />
          <div>
            <div className="text-sm font-medium text-green-600">Capacity OK</div>
            <div className="text-xs text-gray-500">No issues projected</div>
          </div>
        </div>
      )}
      {forecast.bottlenecks[0] && (
        <div className="text-xs text-gray-500">
          First bottleneck: {forecast.bottlenecks[0].resource} in{' '}
          {forecast.bottlenecks[0].monthsUntilFull} months
        </div>
      )}
    </div>
  );
}

function CostSummary({ optimization }: { optimization: CostOptimization }) {
  const savingsPercent = Math.round(
    (optimization.annualSavings /
      (optimization.currentMonthlyCost * 12)) *
      100
  );

  return (
    <div className="space-y-2">
      <div className="flex items-baseline gap-1">
        <span className="text-2xl font-bold text-green-600">
          ${optimization.annualSavings.toLocaleString()}
        </span>
        <span className="text-xs text-gray-500">/year</span>
      </div>
      <div className="text-xs text-gray-500">
        Potential savings ({savingsPercent}% reduction)
      </div>
      <div className="text-xs text-gray-400">
        {optimization.suggestions.length} optimization opportunities
      </div>
    </div>
  );
}

function UpgradeSummary({ paths }: { paths: UpgradePath[] }) {
  const recommended = paths.filter((p) => p.priority === 'recommended').length;
  const critical = paths.filter((p) => p.priority === 'critical').length;

  return (
    <div className="space-y-2">
      {critical > 0 ? (
        <Badge variant="error">{critical} critical upgrades</Badge>
      ) : recommended > 0 ? (
        <Badge variant="warning">{recommended} recommended</Badge>
      ) : (
        <Badge variant="success">Up to date</Badge>
      )}
      {paths[0] && (
        <div className="mt-2 text-xs text-gray-500">
          Top: {paths[0].upgradeTier} (+${paths[0].estimatedCost.toLocaleString()})
        </div>
      )}
    </div>
  );
}

function BottleneckList({ bottlenecks }: { bottlenecks: Bottleneck[] }) {
  const severityColors: Record<string, string> = {
    critical: 'bg-red-100 text-red-700 border-red-200',
    high: 'bg-orange-100 text-orange-700 border-orange-200',
    medium: 'bg-amber-100 text-amber-700 border-amber-200',
    low: 'bg-blue-100 text-blue-700 border-blue-200',
  };

  return (
    <div className="space-y-2">
      {bottlenecks.map((b, i) => (
        <div
          key={i}
          className={clsx('rounded-lg border p-2', severityColors[b.severity])}
        >
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium uppercase">{b.resource}</span>
            <Badge className={severityColors[b.severity]}>{b.severity}</Badge>
          </div>
          <div className="mt-1 flex items-center gap-1 text-xs">
            <Clock className="h-3 w-3" />
            <span>{b.monthsUntilFull} months until capacity</span>
          </div>
          <div className="mt-1 text-xs opacity-80">{b.recommendation}</div>
        </div>
      ))}
    </div>
  );
}

function SuggestionList({
  suggestions,
}: {
  suggestions: CostOptimization['suggestions'];
}) {
  const effortColors: Record<string, string> = {
    low: 'bg-green-100 text-green-700',
    medium: 'bg-amber-100 text-amber-700',
    high: 'bg-red-100 text-red-700',
  };

  return (
    <div className="space-y-2">
      {suggestions.map((s, i) => (
        <div key={i} className="rounded-lg border border-gray-200 bg-gray-50 p-2">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="text-sm font-medium text-gray-900">{s.title}</div>
              <div className="mt-0.5 text-xs text-gray-500">{s.description}</div>
            </div>
            <div className="text-right">
              <div className="text-sm font-bold text-green-600">
                ${s.estimatedSavings}/mo
              </div>
              <Badge className={clsx('mt-1 text-[10px]', effortColors[s.effort])}>
                {s.effort} effort
              </Badge>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function ResourceProjectionChart({ forecast }: { forecast: ResourceForecast }) {
  const resources: Array<{
    key: 'cpu' | 'memory' | 'storage' | 'network';
    label: string;
    color: string;
  }> = [
    { key: 'cpu', label: 'CPU', color: 'bg-blue-500' },
    { key: 'memory', label: 'Memory', color: 'bg-purple-500' },
    { key: 'storage', label: 'Storage', color: 'bg-green-500' },
    { key: 'network', label: 'Network', color: 'bg-orange-500' },
  ];

  // Show months 0, 3, 6, 9, 12
  const displayMonths = [0, 3, 6, 9, 12];
  const data = [
    forecast.currentUsage,
    ...forecast.projectedUsage.filter((_, i) => displayMonths.includes(i + 1)),
  ];

  return (
    <div className="space-y-3">
      {resources.map((resource) => (
        <div key={resource.key} className="space-y-1">
          <div className="flex items-center justify-between text-xs">
            <span className="font-medium text-gray-700">{resource.label}</span>
            <span className="text-gray-500">
              {forecast.projectedUsage[11]?.[resource.key].percent.toFixed(0) || 0}% at 12mo
            </span>
          </div>
          <div className="flex items-center gap-1">
            {data.map((d, i) => (
              <div
                key={i}
                className="relative flex-1 h-6 bg-gray-100 rounded overflow-hidden"
              >
                <div
                  className={clsx(
                    'absolute inset-y-0 left-0 transition-all',
                    resource.color,
                    d[resource.key].percent > 90 && 'animate-pulse'
                  )}
                  style={{ width: `${Math.min(d[resource.key].percent, 100)}%` }}
                />
                <div className="absolute inset-0 flex items-center justify-center text-[10px] font-medium">
                  {d[resource.key].percent.toFixed(0)}%
                </div>
              </div>
            ))}
          </div>
          <div className="flex justify-between text-[9px] text-gray-400">
            <span>Now</span>
            <span>3mo</span>
            <span>6mo</span>
            <span>9mo</span>
            <span>12mo</span>
          </div>
        </div>
      ))}
    </div>
  );
}

function UpgradePathList({ paths }: { paths: UpgradePath[] }) {
  const priorityColors: Record<string, string> = {
    critical: 'border-red-300 bg-red-50',
    recommended: 'border-amber-300 bg-amber-50',
    optional: 'border-gray-200 bg-white',
  };

  const priorityBadges: Record<string, string> = {
    critical: 'bg-red-100 text-red-700',
    recommended: 'bg-amber-100 text-amber-700',
    optional: 'bg-gray-100 text-gray-600',
  };

  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {paths.map((path, i) => (
        <div
          key={i}
          className={clsx('rounded-lg border p-3', priorityColors[path.priority])}
        >
          <div className="flex items-start justify-between">
            <Badge className={priorityBadges[path.priority]}>{path.priority}</Badge>
            <span className="text-sm font-bold text-gray-900">
              +${path.estimatedCost.toLocaleString()}
            </span>
          </div>
          <div className="mt-2">
            <div className="text-xs text-gray-500">{path.currentTier}</div>
            <div className="flex items-center gap-1">
              <span className="text-xs">→</span>
              <span className="text-sm font-medium text-gray-900">
                {path.upgradeTier}
              </span>
            </div>
          </div>
          <div className="mt-2 text-xs text-gray-500">{path.reason}</div>
          <div className="mt-2 space-y-0.5">
            {path.benefits.slice(0, 2).map((benefit, j) => (
              <div key={j} className="flex items-center gap-1 text-[10px] text-gray-500">
                <CheckCircle className="h-2.5 w-2.5 text-green-500" />
                <span>{benefit}</span>
              </div>
            ))}
          </div>
          <div className="mt-2 flex items-center gap-1 text-[10px] text-gray-400">
            <Clock className="h-3 w-3" />
            <span>{path.timeline}</span>
          </div>
        </div>
      ))}
    </div>
  );
}
